import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from 'src/components/layouts/Modal/Modal';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon from 'src/components/ui/Icon/Icon';
import Input from 'src/components/ui/Input/Input';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Select, { Option } from 'src/components/ui/Select/Select';
import { DateTimePicker } from 'src/components/ui/DateTimePicker/DateTimePicker';
import { APP_VERSION } from 'src/constants';
import { useNavigationItems } from 'src/hooks/useNavigationItems';
import { useBugReport } from 'src/providers/BugReportProvider';
import {
  ATTACHMENT_ACCEPT,
  BUG_SEVERITIES,
  BugCategory,
  BugReportResult,
  BugSeverity,
  MAX_ATTACHMENTS,
  MAX_TOTAL_BYTES,
  base64Bytes,
  captureScreenshot,
  categoryForPath,
  fileToBase64,
  mimetypeForFile,
  sendBugReport,
} from 'src/services/bugReport';

type Attachment = { id: number; name: string; mimetype: string; file: string };
let attachmentSeq = 0;

const nowLocal = (): string => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** What the reporter sees after sending: the intake's own sentence, plus number and link when it got through. */
export const ResultToast: React.FC<{ result: BugReportResult; t: TFunction }> = ({ result, t }) => {
  const rejected = result.rejected_attachments.map(a => `${a.name}: ${a.reason}`).join(', ');
  if (result.forward_state === 'sent') {
    return (
      <span>
        {result.message || t('bug_report.sent_remote', { number: result.remote_number })}
        {result.remote_url && (
          <>
            {' '}
            <a href={result.remote_url} target="_blank" rel="noreferrer" className="underline">
              #{result.remote_number}
            </a>
          </>
        )}
        {rejected && !result.message && ` ${t('bug_report.attachments_rejected', { list: rejected })}`}
      </span>
    );
  }
  if (result.forward_state === 'pending') return <span>{t('bug_report.stored_pending', { name: result.name })}</span>;
  if (result.forward_state === 'failed') {
    return (
      <span>
        {t('bug_report.stored_failed', { name: result.name })}
        {result.message ? ` ${result.message}` : ''}
      </span>
    );
  }
  return <span>{t('bug_report.stored', { name: result.name })}</span>;
};

const BugReportModal: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { navigationMenuItems } = useNavigationItems();
  const { isOpen, close, prefill } = useBugReport();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<BugCategory>('other');
  const [where, setWhere] = useState('');
  const [when, setWhen] = useState(nowLocal());
  const [openSeq, setOpenSeq] = useState(0);
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [preceding, setPreceding] = useState('');
  const [severity, setSeverity] = useState<BugSeverity | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const attachmentInput = useRef<HTMLInputElement>(null);

  const categories = useMemo<BugCategory[]>(() => {
    const fromMenu = navigationMenuItems
      .map(item => categoryForPath(`/${item.link ?? ''}`))
      .filter(key => key !== 'other');
    const unique = Array.from(new Set<BugCategory>([...fromMenu, 'settings']));
    return [...unique, 'other'];
  }, [navigationMenuItems]);

  const categoryOptions = useMemo<Option[]>(
    () => categories.map(key => ({ value: key, label: t(`bug_report.categories.${key}`) })),
    [categories, t],
  );
  const severityOptions = useMemo<Option[]>(
    () => BUG_SEVERITIES.map(key => ({ value: key, label: t(`bug_report.severities.${key}`) })),
    [t],
  );

  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;

  useEffect(() => {
    if (!isOpen) return;
    const fromRoute = categoryForPath(location.pathname);
    setTitle(prefill.title ?? '');
    setActual(prefill.actual ?? '');
    setExpected('');
    setPreceding('');
    setWhere('');
    setSeverity(null);
    setAttachments([]);
    setCategory(categoriesRef.current.includes(fromRoute) ? fromRoute : 'other');
    setWhen(nowLocal());
    setOpenSeq(seq => seq + 1);
  }, [isOpen, prefill, location.pathname]);

  const totalBytes = attachments.reduce((n, a) => n + base64Bytes(a.file), 0);
  const whereRequired = category === 'other';
  const valid =
    title.trim().length >= 3 &&
    expected.trim().length >= 3 &&
    actual.trim().length >= 3 &&
    !!when &&
    !!severity &&
    (!whereRequired || where.trim().length > 0) &&
    totalBytes <= MAX_TOTAL_BYTES;

  const addAttachments = (added: Attachment[]) => {
    const room = MAX_ATTACHMENTS - attachments.length;
    if (added.length > room) toast.warning(t('bug_report.attachments_max', { count: MAX_ATTACHMENTS }));
    setAttachments(prev => [...prev, ...added.slice(0, Math.max(0, room))]);
  };

  const capturePage = async () => {
    if (capturing) return;
    setCapturing(true);
    const shot = await captureScreenshot();
    setCapturing(false);
    if (!shot) {
      toast.error(t('bug_report.screenshot_failed'));
      return;
    }
    attachmentSeq += 1;
    addAttachments([{ id: attachmentSeq, name: `screenshot-${attachmentSeq}.png`, mimetype: 'image/png', file: shot }]);
  };

  const onAttachmentFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (!files.length) return;
    const accepted = files.filter(file => mimetypeForFile(file));
    if (accepted.length < files.length) toast.warning(t('bug_report.attachment_type_refused'));
    const added = await Promise.all(
      accepted.map(async file => {
        attachmentSeq += 1;
        return {
          id: attachmentSeq,
          name: file.name,
          mimetype: mimetypeForFile(file) as string,
          file: await fileToBase64(file),
        };
      }),
    );
    addAttachments(added);
  };

  const submit = async () => {
    if (!valid || busy || !severity) return;
    setBusy(true);
    try {
      const result = await sendBugReport({
        title: title.trim(),
        category,
        where_happened: whereRequired ? where.trim() : '',
        happened_at: new Date(when).toISOString(),
        expected_result: expected.trim(),
        actual_result: actual.trim(),
        preceding_actions: preceding.trim(),
        severity,
        app_version: APP_VERSION,
        attachments: attachments.map(({ name, mimetype, file }) => ({ name, mimetype, file })),
      });
      const content = <ResultToast result={result} t={t} />;
      if (result.forward_state === 'failed') toast.warning(content, { autoClose: 15000 });
      else toast.success(content, { autoClose: result.rejected_attachments.length ? 15000 : 8000 });
      close();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const label = 'flex flex-col gap-8 typo-text-14--bold text-faded';

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <div className="w-[720px] max-w-[92vw] max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center gap-8 px-24 py-16 border-subtle-bottom">
          <Icon name="bug" width={22} height={22} className="text-full" />
          <h4 className="typo-title-24 text-full">{t('bug_report.title')}</h4>
        </div>
        <div className="flex flex-col gap-12 px-24 py-16 overflow-y-auto">
          <div className={label}>
            <label htmlFor="bug-summary">{t('bug_report.summary')} *</label>
            <Input
              id="bug-summary"
              placeholder={t('bug_report.summary')}
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="grid grid-cols-2 gap-12">
            <div className={label}>
              <label htmlFor="bug-category">{t('bug_report.category')} *</label>
              <Select
                id="bug-category"
                fullWidth
                menuFixed
                options={categoryOptions}
                defaultValue={categoryOptions.find(option => option.value === category)}
                onChange={(option: Option) => option && setCategory(option.value as BugCategory)}
              />
            </div>
            <div className={label}>
              <span>{t('bug_report.when')} *</span>
              <DateTimePicker
                key={`when-${openSeq}`}
                defaultValue={when ? dayjs(when) : null}
                onChange={value => setWhen(value ?? '')}
              />
            </div>
          </div>
          {whereRequired && (
            <div className={label}>
              <label htmlFor="bug-where">{t('bug_report.where')} *</label>
              <Input
                id="bug-where"
                placeholder={t('bug_report.where_hint')}
                value={where}
                onChange={e => setWhere(e.target.value)}
                maxLength={120}
              />
            </div>
          )}
          <div className={label}>
            <label htmlFor="bug-severity">{t('bug_report.severity')} *</label>
            <Select
              id="bug-severity"
              fullWidth
              menuFixed
              options={severityOptions}
              placeholder={t('bug_report.severity_placeholder')}
              defaultValue={severityOptions.find(option => option.value === severity)}
              onChange={(option: Option) => setSeverity(option ? (option.value as BugSeverity) : null)}
            />
          </div>
          <div className={label}>
            <label htmlFor="bug-expected">{t('bug_report.expected')} *</label>
            <Textarea
              id="bug-expected"
              minRows={2}
              value={expected}
              onChange={e => setExpected(e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className={label}>
            <label htmlFor="bug-actual">{t('bug_report.actual')} *</label>
            <Textarea
              id="bug-actual"
              minRows={2}
              value={actual}
              onChange={e => setActual(e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className={label}>
            <label htmlFor="bug-preceding">{t('bug_report.preceding')}</label>
            <Textarea
              id="bug-preceding"
              minRows={2}
              value={preceding}
              onChange={e => setPreceding(e.target.value)}
              maxLength={4000}
            />
          </div>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <span className="typo-text-12--bold text-faded">{t('bug_report.attachments')}</span>
              <span className="typo-text-12 text-faded">{t('bug_report.screenshots_hint')}</span>
            </div>
            <input
              ref={attachmentInput}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={onAttachmentFiles}
            />
            {attachments.map(attachment => (
              <div
                key={attachment.id}
                className="flex items-center gap-12 rounded-base border-subtle surface-base px-12 py-8">
                {attachment.mimetype.startsWith('image/') ? (
                  <img
                    src={`data:${attachment.mimetype};base64,${attachment.file}`}
                    alt={attachment.name}
                    className="h-[48px] w-[72px] object-cover rounded-base border-subtle"
                  />
                ) : (
                  <Icon name="file" width={18} height={18} className="text-faded" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="typo-text-14--medium text-full truncate">{attachment.name}</div>
                  <div className="typo-text-12 text-faded">{formatSize(base64Bytes(attachment.file))}</div>
                </div>
                <button
                  type="button"
                  aria-label={`${t('general.remove')} ${attachment.name}`}
                  className="text-full border-none bg-transparent cursor-pointer"
                  onClick={() => setAttachments(prev => prev.filter(a => a.id !== attachment.id))}>
                  <Icon name="deleteCircle" width={20} height={20} />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-8 flex-wrap">
              <Button
                tone={ButtonToneProps.SECONDARY}
                title={capturing ? t('general.loading') : t('bug_report.capture_page')}
                onClick={capturePage}
                disabled={capturing || attachments.length >= MAX_ATTACHMENTS}
              />
              <Button
                tone={ButtonToneProps.SECONDARY}
                title={t('bug_report.attachments_add')}
                onClick={() => attachmentInput.current?.click()}
                disabled={attachments.length >= MAX_ATTACHMENTS}
              />
              <span className={`typo-text-12 ${totalBytes > MAX_TOTAL_BYTES ? 'status-error-full' : 'text-faded'}`}>
                {t('bug_report.attachments_hint', { size: formatSize(totalBytes), max: MAX_TOTAL_BYTES / 1024 / 1024 })}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-8 px-24 py-16 border-subtle-top">
          <Button tone={ButtonToneProps.SECONDARY} title={t('general.cancel')} onClick={close} />
          <Button
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={busy ? t('general.loading') : t('bug_report.send')}
            onClick={submit}
            disabled={!valid || busy}
          />
        </div>
      </div>
    </Modal>
  );
};

export default BugReportModal;
